/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createContext, useContext, useRef } from "react";
import { StateCreator, StoreApi, createStore, useStore } from "zustand";
import { registerResettableStore } from '@/shared/state/store-registry';

/** Injected by the `storybook-smoke` Vitest project only. */
declare const __STORY_SMOKE__: boolean | undefined;

/**
 * createLocalZustand function.
 *
 * @param {*} initializer - Parameter description
 *
 * @returns {*} Return value description
 */
export function createLocalZustand<T>(
/**
 * initializer function.
 *
 * @param {*} initialState - Parameter description
 *
 * @returns {*} Return value description
 */
  initializer: (initialState: Partial<T>) => StateCreator<T, [], [], T>
) {
  type GenericStoreApi = StoreApi<T>;

  const StoreContext = createContext<GenericStoreApi | null>(null);

  const ContextProvider: ComponentWithChildren = ({
    children,
    initialState,
  }) => {
    const storeRef = useRef<GenericStoreApi | undefined>(undefined);
    if (!storeRef.current)
      storeRef.current = createStore(initializer(initialState ?? {}));

    return (
      <StoreContext.Provider value={storeRef.current}>
        {children}
      </StoreContext.Provider>
    );
  };

  type SelectorFn<U> = (state: T) => U;
  // The `test` fallback keeps unit tests from having to wrap every component in
  // a provider. It also hides a real bug class: a component that reads the
  // store outside a provider throws in the browser but passes under Vitest.
  // Four Storybook stories shipped broken that way. `__STORY_SMOKE__` is defined
  // only by the `storybook-smoke` project in `vitest.config.ts`, which mounts
  // every story; turning the fallback off there restores production semantics
  // so that project actually catches a missing provider.
  const isStorySmokeRun =
    typeof __STORY_SMOKE__ !== 'undefined' && __STORY_SMOKE__ === true;
  const fallbackStore =
    typeof import.meta !== 'undefined' &&
    (import.meta as any).env?.MODE === 'test' &&
    !isStorySmokeRun
      ? registerResettableStore(createStore(initializer({})))
      : null;

  const useLocalStore = <U,>(selector: SelectorFn<U>): U => {
    const store = useContext(StoreContext);
    if (!store) {
      if (fallbackStore) {
        return useStore(fallbackStore, selector);
      }
      throw new Error("Missing StoreProvider");
    }
    return useStore(store, selector);
  };

  return {
    Context: StoreContext,
    Provider: ContextProvider,
    Consumer: StoreContext.Consumer,
    useStore: useLocalStore,
  };
}

export default createLocalZustand;
